'use client';

import React, { isValidElement, useId } from 'react';
import { InputSelect, type SelectOption } from '../InputSelect/InputSelect';
import { InputText } from '../InputText/InputText';
import { Tabs } from '../Tabs/Tabs';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import './BlockCard.css';

export interface BlockCardTab {
  id: 'content' | 'settings';
  label: string;
}

export interface BlockCardProps {
  activeTabId?: 'content' | 'settings';
  onTabChange?: (tabId: 'content' | 'settings') => void;
  className?: string;
  children?: React.ReactNode;
}

interface SlotProps {
  children?: React.ReactNode;
  className?: string;
}

const Title: React.FC<SlotProps> & { slot?: 'title' } = ({ children, className = '' }) => (
  <div className={`block-card__title ${className}`.trim()}>{children}</div>
);
Title.displayName = 'BlockCard.Title';
Title.slot = 'title';

const Actions: React.FC<SlotProps> & { slot?: 'actions' } = ({ children, className = '' }) => (
  <div className={`block-card__actions ${className}`.trim()}>{children}</div>
);
Actions.displayName = 'BlockCard.Actions';
Actions.slot = 'actions';

const Content: React.FC<SlotProps> & { slot?: 'content' } = ({ children, className = '' }) => (
  <div className={`block-card__content ${className}`.trim()}>{children}</div>
);
Content.displayName = 'BlockCard.Content';
Content.slot = 'content';

const Settings: React.FC<SlotProps> & { slot?: 'settings' } = ({ children, className = '' }) => (
  <div className={`block-card__settings ${className}`.trim()}>{children}</div>
);
Settings.displayName = 'BlockCard.Settings';
Settings.slot = 'settings';

function extractSlots(children: React.ReactNode) {
  let titleNode: React.ReactNode | null = null;
  let actionsNode: React.ReactNode | null = null;
  let contentNode: React.ReactNode | null = null;
  let settingsNode: React.ReactNode | null = null;

  React.Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const type = child.type as { slot?: string };
    switch (type.slot) {
      case 'title':
        titleNode = child;
        break;
      case 'actions':
        actionsNode = child;
        break;
      case 'content':
        contentNode = child;
        break;
      case 'settings':
        settingsNode = child;
        break;
      default:
        break;
    }
  });

  return { titleNode, actionsNode, contentNode, settingsNode };
}

const BlockCardInternal: React.FC<BlockCardProps> = ({
  activeTabId = 'content',
  onTabChange,
  className = '',
  children,
}) => {
  const instanceId = useId();

  const defaultOptions: SelectOption<string>[] = [
    { value: 'nps', label: 'NPS Rating' },
    { value: 'rating-1-5', label: 'Rating 1-5' },
    { value: 'rating-0-10', label: 'Rating 0-10' },
  ];

  const { titleNode, actionsNode, contentNode, settingsNode } = extractSlots(children);
  const hasContent = Boolean(contentNode);
  const hasSettings = Boolean(settingsNode);
  const showTabs = hasContent && hasSettings;

  const resolvedTitle = titleNode ?? <Title>Question Element:</Title>;
  const resolvedActions = actionsNode ?? (
    <Actions>
      <small>Add Actions...</small>
    </Actions>
  );

  const defaultContent = (
    <Content>
      <div className="block-card__grid">
        <div>
          <InputSelect
            id={`bc-type-${instanceId}`}
            label="Type"
            value={null}
            onChange={() => {}}
            options={defaultOptions}
            placeholder="Select type"
          />
        </div>
        <div>
          <InputText
            id={`bc-text-${instanceId}`}
            label="Text"
            placeholder="What could we do to improve?"
            value={''}
            onChange={() => {}}
          />
        </div>
      </div>
    </Content>
  );

  const resolvedContent = contentNode ?? defaultContent;

  const bodyStatic = (() => {
    if (hasContent) return resolvedContent;
    if (hasSettings) return settingsNode;
    return resolvedContent;
  })();

  return (
    <motion.div
      className={`block-card ${className}`.trim()}
      layout
      transition={{ layout: { duration: 0.36, ease: [0.12, 0.9, 0.1, 1] } }}
    >
      <motion.div className="block-card__header" layout="position">
        <div className="block-card__header-left">
          {resolvedTitle}
          {showTabs && (
            <Tabs
              ariaLabel="Block tabs"
              size="sm"
              items={[
                { id: 'content', label: 'Content' },
                { id: 'settings', label: 'Settings' },
              ]}
              value={activeTabId}
              onValueChange={(v) => v && onTabChange?.(v as 'content' | 'settings')}
            />
          )}
        </div>

        {resolvedActions}
      </motion.div>

      <div className="block-card__body">
        <LayoutGroup id={`block-card-${instanceId}-layout`}>
          <motion.div
            layout
            transition={{ layout: { duration: 0.36, ease: [0.12, 0.9, 0.1, 1] } }}
            style={{ willChange: 'transform' }}
          >
            {showTabs ? (
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={activeTabId}
                  layout
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -10, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.12, 0.9, 0.1, 1] }}
                  style={{ willChange: 'transform, opacity' }}
                >
                  {activeTabId === 'content' ? resolvedContent : (settingsNode ?? <Settings />)}
                </motion.div>
              </AnimatePresence>
            ) : (
              bodyStatic
            )}
          </motion.div>
        </LayoutGroup>
      </div>
    </motion.div>
  );
};

export const BlockCard = Object.assign(BlockCardInternal, {
  Title,
  Actions,
  Content,
  Settings,
});

BlockCard.displayName = 'BlockCard';

export default BlockCard;
